import React from 'react'
import { motion } from 'framer-motion'

export interface SectionWithMockupProps {
  title: string | React.ReactNode
  description: string | React.ReactNode
  primaryImageSrc: string
  secondaryImageSrc: string
  reverseLayout?: boolean
  id?: string
  showBottomLine?: boolean
}

const SectionWithMockup: React.FC<SectionWithMockupProps> = ({
  title,
  description,
  primaryImageSrc,
  secondaryImageSrc,
  reverseLayout = false,
  id,
  showBottomLine = true,
}) => {
  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.2 },
    },
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] as any } },
  }
  const layoutClasses = reverseLayout ? 'md:grid-cols-2 md:grid-flow-col-dense' : 'md:grid-cols-2'
  const textOrderClass = reverseLayout ? 'md:col-start-2' : ''
  const imageOrderClass = reverseLayout ? 'md:col-start-1' : ''
  const imageSpacingClass = reverseLayout ? 'lg:mr-10 xl:mr-16' : 'lg:ml-10 xl:ml-16'

  return (
    <section id={id} className="relative py-24 md:py-32 bg-background overflow-hidden">
      <div className="w-full max-w-[1220px] px-6 md:px-10 relative z-10 mx-auto">
        <motion.div
          className={`grid grid-cols-1 gap-16 md:gap-16 lg:gap-24 w-full items-center ${layoutClasses}`}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div
            className={`flex flex-col items-start gap-4 mt-10 md:mt-0 max-w-[546px] mx-auto md:mx-0 ${textOrderClass}`}
            variants={itemVariants}
          >
            <div className="space-y-2 md:space-y-1">
              <h2 className="text-3xl md:text-[40px] font-semibold leading-tight md:leading-[53px]">
                {title}
              </h2>
            </div>
            <p className="text-foreground/70 text-sm md:text-[15px] leading-6">{description}</p>
          </motion.div>

          <motion.div
            className={`relative mt-10 md:mt-0 mx-auto ${imageOrderClass} ${imageSpacingClass} w-full max-w-[300px] md:max-w-[471px]`}
            variants={itemVariants}
          >
            <motion.div
              className={`absolute w-[300px] h-[317px] md:w-[472px] md:h-[500px] bg-muted rounded-[32px] z-0`}
              style={{
                top: reverseLayout ? 'auto' : '10%',
                bottom: reverseLayout ? '10%' : 'auto',
                left: reverseLayout ? 'auto' : '-20%',
                right: reverseLayout ? '-20%' : 'auto',
                transform: reverseLayout ? 'translate(0, 0)' : 'translateY(10%)',
                filter: 'blur(2px)',
              }}
              initial={{ y: reverseLayout ? 0 : 0 }}
              whileInView={{ y: reverseLayout ? -20 : -30 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] as any }}
              viewport={{ once: true, amount: 0.5 }}
            >
              <div
                className="relative w-full h-full bg-cover bg-center rounded-[32px]"
                style={{ backgroundImage: `url(${secondaryImageSrc})` }}
              />
            </motion.div>

            <motion.div
              className="relative w-full h-[405px] md:h-[637px] bg-background/10 rounded-[32px] backdrop-blur-[15px] border-0 z-10 overflow-hidden"
              initial={{ y: reverseLayout ? 0 : 0 }}
              whileInView={{ y: reverseLayout ? 20 : 30 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] as any, delay: 0.1 }}
              viewport={{ once: true, amount: 0.5 }}
            >
              <div className="h-full">
                <div className="h-full relative" style={{ backgroundSize: '100% 100%' }}>
                  <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${primaryImageSrc})` }} />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      {showBottomLine && (
        <div
          className="absolute w-full h-px bottom-0 left-0 z-0"
          style={{
            background:
              'radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 100%)',
          }}
        />
      )}
    </section>
  )
}

export default SectionWithMockup
